import React, { useState } from "react";
import { Outlet, Route, Routes, Navigate } from "react-router-dom";
import PageHeader from "../../components/ui/PageHeader";
import { Megaphone } from "lucide-react";
import { MarketingProvider, useMarketing } from "../../context/MarketingContext";
import EmailTemplatesListPage from "./email-templates/EmailTemplatesListPage";
import CreateEmailTemplatePage from "./email-templates/CreateEmailTemplatePage";
import EditEmailTemplatePage from "./email-templates/EditEmailTemplatePage";
import SMSTemplatesListPage from "./sms-templates/SMSTemplatesListPage";
import CreateSMSTemplatePage from "./sms-templates/CreateSMSTemplatePage";
import EditSMSTemplatePage from "./sms-templates/EditSMSTemplatePage";
import CampaignsListPage from "./campaigns/CampaignsListPage";
import CreateCampaignPage from "./campaigns/CreateCampaignPage";
import EditCampaignPage from "./campaigns/EditCampaignPage";
import CampaignSchedulingPage from "./campaign-scheduling/CampaignSchedulingPage";
import { Button } from "../../components/ui/Button";
import { RechargeCreditsDrawer } from "../../components/marketing/RechargeCreditsDrawer";

const CreditPanel: React.FC = () => {
  const { remainingCredits } = useMarketing();
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <>
      <div className="flex flex-col items-end gap-2">
        <div className="inline-flex items-center rounded-full bg-purple-50 px-3 py-1 text-xs font-semibold text-purple-700 border border-purple-100 shadow-sm">
          <span className="mr-1 h-2 w-2 rounded-full bg-emerald-500" />
          <span>Credit balance</span>
          <span className="ml-2 text-sm font-bold">{remainingCredits.toFixed(2)}</span>
        </div>
        <Button
          type="button"
          size="sm"
          className="bg-[#885ABB] text-white hover:bg-[#6f47a2]"
          onClick={() => setDrawerOpen(true)}
        >
          Recharge Credits
        </Button>
      </div>

      <RechargeCreditsDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />
    </>
  );
};

const MarketingShell: React.FC = () => {
  return (
    <div className="min-h-full">
      <PageHeader
        title="Marketing"
        description="Manage your email and SMS templates and run targeted campaigns."
        icon={Megaphone}
        variant="card"
        mobileActionsInHeader={false}
        actions={<CreditPanel />}
      />
      <div className="mt-4">
        <Outlet />
      </div>
    </div>
  );
};

const MarketingRoutes: React.FC = () => {
  return (
    <Routes>
      <Route element={<MarketingShell />}>
        <Route
          index
          element={<Navigate to="/marketing/email-templates" replace />}
        />
        <Route path="email-templates" element={<EmailTemplatesListPage />} />
        <Route path="email-templates/create" element={<CreateEmailTemplatePage />} />
        <Route path="email-templates/:id/edit" element={<EditEmailTemplatePage />} />
        <Route path="sms-templates" element={<SMSTemplatesListPage />} />
        <Route path="sms-templates/create" element={<CreateSMSTemplatePage />} />
        <Route path="sms-templates/:id/edit" element={<EditSMSTemplatePage />} />
        <Route path="campaigns" element={<CampaignsListPage />} />
        <Route path="campaigns/create" element={<CreateCampaignPage />} />
        <Route path="campaigns/:id/edit" element={<EditCampaignPage />} />
        <Route path="campaign-scheduling" element={<CampaignSchedulingPage />} />
      </Route>
    </Routes>
  );
};

const MarketingLayout: React.FC = () => {
  return (
    <MarketingProvider>
      <MarketingRoutes />
    </MarketingProvider>
  );
};

export default MarketingLayout;

